import { cn } from '@/internationalization/cn'
import { APP_NAME } from '@/internationalization/constants'

/**
 * The Sabha mark and lockup.
 *
 * The artwork is INLINE rather than an `<img src="/brand/…">` for three
 * reasons: it is the first thing painted on the sign-in page and a separate
 * request would flash an empty box; the tile has to change colour with the
 * theme, which an `<img>` cannot do; and an inline `<svg>` scales from the
 * 20 px collapsed sidebar to the 40 px auth header with no second asset.
 *
 * The files in `public/brand/` are the same artwork for the places a URL is
 * required — the favicon, the web manifest, an email, a press kit.
 *
 * The tile colour is a TOKEN (`--sb-logo`), not the raw #2F3A73, because the
 * mark's navy is nearly the dark canvas and would disappear into it. tokens.css
 * lightens it for dark mode while keeping the glyph white; see
 * docs/DESIGN-TOKENS.md.
 */

/** Letterform paths, from `sabha-app-icon.svg` (512 viewBox). Do not edit. */
const GLYPH =
  'M254.25 337.62 219.34 358.25 161.24 270.96Q155.85 263.02 153.63 257.63Q151.40 252.23 151.40 247.15' +
  'Q151.40 238.90 158.23 234.13Q165.05 229.37 176.48 229.37L181.88 236.36Q188.23 245.24 197.11 245.24' +
  'Q206.64 245.24 212.83 237.94Q219.02 230.64 219.02 216.04Q219.02 203.34 215.21 193.82' +
  'Q211.40 184.30 207.27 178.90H142.20V149.06H369.80V178.90H343.45V351.90H304.41V276.67' +
  'Q299.97 278.58 292.98 279.85Q286.00 281.12 275.21 281.12Q263.78 281.12 252.03 277.94' +
  'Q240.29 274.77 231.72 269.37Q226.00 272.86 219.81 274.29Q213.62 275.72 207.59 275.72H205.37Z' +
  'M270.76 249.05Q282.82 249.05 290.92 246.51Q299.01 243.98 304.41 239.53V178.90H246.95' +
  'Q250.44 183.66 254.57 194.14Q258.70 204.61 258.70 219.85Q258.70 227.47 257.11 234.29' +
  'Q255.52 241.12 252.98 246.51Q257.11 248.10 261.71 248.58Q266.32 249.05 270.76 249.05Z'

/**
 * The app icon on its own — a rounded tile with the Sabha letterform.
 *
 * Size it with a Tailwind height/width pair; it is square and has no intrinsic
 * dimensions. Decorative by default: every place it is used already carries the
 * name in adjacent text or an `sr-only` label, and a second announcement of
 * "Sabha" is noise to a screen reader.
 */
export function LogoMark({ className, title }: { className?: string; title?: string }) {
  return (
    <svg
      viewBox="0 0 512 512"
      className={cn('shrink-0 text-logo', className)}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <rect width="512" height="512" rx="119.8" fill="currentColor" />
      <path d={GLYPH} className="fill-logo-fg" />
      <rect x="142.2" y="151.4" width="227.6" height="29.77" className="fill-logo-fg" />
    </svg>
  )
}

/**
 * Mark plus name — what the sidebar, topbar and auth pages show.
 *
 * The name is `APP_NAME` text rather than the wordmark SVG on purpose: the
 * deployment can rename the product through `VITE_APP_NAME`, and a baked-in
 * wordmark would leave a renamed install showing two different names side by
 * side.
 */
export function Logo({
  size = 'md',
  showName = true,
  className,
}: {
  size?: 'sm' | 'md' | 'lg'
  showName?: boolean
  className?: string
}) {
  const mark = { sm: 'h-6 w-6', md: 'h-7 w-7', lg: 'h-9 w-9' }[size]
  const text = { sm: 'text-base', md: 'text-md', lg: 'text-lg' }[size]

  return (
    <span className={cn('flex items-center gap-2', className)}>
      <LogoMark className={mark} />
      {showName ? (
        <span className={cn('truncate font-semibold tracking-tight text-fg', text)}>
          {APP_NAME}
        </span>
      ) : (
        <span className="sr-only">{APP_NAME}</span>
      )}
    </span>
  )
}
